import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Eye, X, Upload, Lock } from 'lucide-react';
import { useAuth } from '../AuthContext';
import Papa from 'papaparse';

const BACKEND_URL = process.env.REACT_APP_API_URL || 'https://quizzler-production.up.railway.app';

export default function MyCustomGames() {
  const { user } = useAuth();
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGame, setSelectedGame] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    questions: []
  });

  // Check if user has access (Regular Host or Pro Host)
  const hasAccess = user?.tier === 'Regular Host' || user?.tier === 'Pro Host';

  useEffect(() => {
    if (hasAccess) {
      fetchGames();
    } else {
      setLoading(false);
    }
  }, [hasAccess]);

  const fetchGames = async () => {
    try {
      const token = localStorage.getItem('quizzler_host_token');
      const response = await fetch(`${BACKEND_URL}/api/host/custom-games`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setGames(data.games || []);
      }
    } catch (error) {
      console.error('Error fetching custom games:', error);
    } finally {
      setLoading(false);
    }
  };

  const getEmptyQuestions = () => {
    return Array(17).fill(null).map((_, idx) => ({
      number: idx + 1,
      category: '',
      text: '',
      answer: '',
      type: idx === 7 ? 'visual' : idx === 16 ? 'final' : 'regular',
      imageUrl: ''
    }));
  };

  const handleCreate = () => {
    setIsEditing(false);
    setFormData({
      title: '',
      description: '',
      questions: getEmptyQuestions()
    });
    setShowModal(true);
  };

  const handleEdit = async (game) => {
    try {
      const token = localStorage.getItem('quizzler_host_token');
      const response = await fetch(`${BACKEND_URL}/api/host/custom-games/${game.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFormData({
          title: data.game.title,
          description: data.game.description || '',
          questions: data.game.questions || getEmptyQuestions()
        });
        setSelectedGame(data.game);
        setIsEditing(true);
        setShowModal(true);
      }
    } catch (error) {
      console.error('Error fetching game:', error);
    }
  };

  const handleView = async (game) => {
    try {
      const token = localStorage.getItem('quizzler_host_token');
      const response = await fetch(`${BACKEND_URL}/api/host/custom-games/${game.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedGame(data.game);
        setShowModal(false);
      }
    } catch (error) {
      console.error('Error fetching game:', error);
    }
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('quizzler_host_token');
      const url = isEditing 
        ? `${BACKEND_URL}/api/host/custom-games/${selectedGame.id}`
        : `${BACKEND_URL}/api/host/custom-games`;
      
      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        alert(isEditing ? 'Game updated!' : 'Game created!');
        setShowModal(false);
        fetchGames();
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to save game');
      }
    } catch (error) {
      console.error('Error saving game:', error);
      alert('Failed to save game');
    }
  };

  const handleDelete = async (game) => {
    if (!window.confirm(`Are you sure you want to delete "${game.title}"?`)) return;

    try {
      const token = localStorage.getItem('quizzler_host_token');
      const response = await fetch(`${BACKEND_URL}/api/host/custom-games/${game.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        alert('Game deleted');
        fetchGames();
      } else {
        alert('Failed to delete game');
      }
    } catch (error) {
      console.error('Error deleting game:', error);
    }
  };

  const handleImportCSV = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const imported = results.data;
        const newQuestions = getEmptyQuestions();

        // Questions 1-7 (rows 0-6)
        for (let i = 0; i < 7 && i < imported.length; i++) {
          if (imported[i].Category && imported[i].Question && imported[i].Answer) {
            newQuestions[i] = {
              number: i + 1,
              category: imported[i].Category,
              text: imported[i].Question,
              answer: imported[i].Answer,
              type: 'regular',
              imageUrl: imported[i]['Image URL'] || ''
            };
          }
        }

        // Visual Round (row 7)
        if (imported.length > 7) {
          newQuestions[7] = {
            number: 8,
            category: imported[7].Category || '',
            text: imported[7].Question || '',
            answer: imported[7].Answer || '',
            type: 'visual',
            imageUrl: imported[7]['Image URL'] || ''
          };
        }

        // Questions 8-15 (rows 8-15 in CSV)
        for (let i = 8; i <= 15 && i < imported.length; i++) {
          if (imported[i].Category && imported[i].Question && imported[i].Answer) {
            newQuestions[i] = {
              number: i + 1,
              category: imported[i].Category,
              text: imported[i].Question,
              answer: imported[i].Answer,
              type: 'regular',
              imageUrl: imported[i]['Image URL'] || ''
            };
          }
        }

        // Final question (row 16)
        if (imported.length >= 17 && imported[16]) {
          newQuestions[16] = {
            number: 17,
            category: imported[16].Category || '',
            text: imported[16].Question || '',
            answer: imported[16].Answer || '',
            type: 'final',
            imageUrl: ''
          };
        }

        setFormData({ ...formData, questions: newQuestions });
        alert(`Successfully imported ${imported.length} questions from CSV!`);
      },
      error: (error) => {
        alert('Error parsing CSV: ' + error.message);
      }
    });

    event.target.value = '';
  };

  const updateQuestion = (index, field, value) => {
    const updated = [...formData.questions];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, questions: updated });
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-xl text-gray-600">Loading your games...</div>
      </div>
    );
  }

  // If no access, show upgrade message
  if (!hasAccess) {
    return (
      <div className="p-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-8">My Custom Games</h1>
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <Lock size={64} className="mx-auto mb-4 text-gray-400" />
          <h3 className="text-xl font-bold text-gray-800 mb-2">Feature Locked</h3>
          <p className="text-gray-600 mb-6">
            Custom Games are available for Regular Host and Pro Host subscribers.
          </p>
          <a 
            href="/dashboard/subscription"
            className="inline-block bg-teal-600 text-white px-6 py-3 rounded-lg hover:bg-teal-700 transition"
          >
            Upgrade Your Plan
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-800">My Custom Games</h1>
        <button
          onClick={handleCreate}
          className="bg-teal-600 text-white px-6 py-2 rounded-lg hover:bg-teal-700 transition flex items-center gap-2"
        >
          <Plus size={20} /> Create Game
        </button>
      </div>

      {games.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <div className="text-6xl mb-4">🎮</div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">No Custom Games Yet</h3>
          <p className="text-gray-600 mb-6">Create your first custom trivia game to use at your venues.</p>
          <button
            onClick={handleCreate}
            className="bg-teal-600 text-white px-6 py-3 rounded-lg hover:bg-teal-700 transition"
          >
            Create Your First Game
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {games.map((game) => (
            <div
              key={game.id}
              className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition"
            >
              <div className="inline-block bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-xs font-semibold mb-3">
                Custom
              </div>

              <h3 className="text-xl font-bold text-gray-800 mb-2">{game.title}</h3>
              <p className="text-gray-600 text-sm mb-4">{game.description || 'No description'}</p>
              
              <div className="text-sm text-gray-500 mb-4">
                {game.question_count || 0} questions
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => handleView(game)}
                  className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200 transition flex items-center justify-center gap-1"
                >
                  <Eye size={16} /> View
                </button>
                <button
                  onClick={() => handleEdit(game)}
                  className="flex-1 bg-teal-100 text-teal-700 py-2 rounded-lg hover:bg-teal-200 transition flex items-center justify-center gap-1"
                >
                  <Pencil size={16} /> Edit
                </button>
                <button
                  onClick={() => handleDelete(game)}
                  className="bg-red-100 text-red-700 py-2 px-3 rounded-lg hover:bg-red-200 transition"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* View Modal */}
      {selectedGame && !showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-2">{selectedGame.title}</h2>
                <p className="text-gray-600">{selectedGame.description}</p>
              </div>
              <button
                onClick={() => setSelectedGame(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Questions</h3>
              {selectedGame.questions?.map((q, idx) => (
                <div key={idx} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                      q.type === 'visual' ? 'bg-orange-100 text-orange-700' :
                      q.type === 'final' ? 'bg-red-100 text-red-700' :
                      'bg-teal-100 text-teal-700'
                    }`}>
                      {q.type === 'visual' ? 'Visual' : q.type === 'final' ? 'Final' : `Q${idx + 1}`}
                    </span>
                    <div className="flex-1">
                      <p className="text-sm text-gray-500 mb-1">{q.category}</p>
                      <p className="font-semibold text-gray-800 mb-2">{q.text}</p>
                      <p className="text-sm text-gray-600">
                        <span className="font-semibold">Answer:</span> {q.answer}
                      </p>
                      {q.type === 'visual' && q.imageUrl && (
                        <p className="text-sm text-orange-600 mt-1">
                          <span className="font-semibold">Image:</span> {q.imageUrl}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={() => setSelectedGame(null)}
              className="w-full mt-6 bg-gray-200 hover:bg-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold transition"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-2xl font-bold text-gray-800">
                {isEditing ? 'Edit Game' : 'Create Custom Game'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Game Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="e.g., Thursday Night Trivia - Week 1"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Optional description..."
                  rows={2}
                />
              </div>
            </div>

            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-800">Questions (17 total)</h3>
              <label className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition cursor-pointer flex items-center gap-2">
                <Upload size={18} /> Import CSV
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleImportCSV}
                  className="hidden"
                />
              </label>
            </div>

            <div className="space-y-4 max-h-96 overflow-y-auto">
              {formData.questions.map((q, idx) => (
                <div key={idx} className={`border rounded-lg p-4 ${
                  q.type === 'visual' ? 'border-orange-300 bg-orange-50' :
                  q.type === 'final' ? 'border-red-300 bg-red-50' :
                  'border-gray-200'
                }`}>
                  <div className="flex items-center justify-between mb-3">
                    <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                      q.type === 'visual' ? 'bg-orange-200 text-orange-800' :
                      q.type === 'final' ? 'bg-red-200 text-red-800' :
                      'bg-teal-100 text-teal-700'
                    }`}>
                      {q.type === 'visual' ? 'Visual Round' : q.type === 'final' ? 'Final Question' : `Q${idx + 1}`}
                    </span>
                    {q.type === 'visual' && (
                      <a 
                        href="https://quizzlertrivia.com/gridbuild/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-orange-500 text-white px-3 py-1 rounded-lg text-sm hover:bg-orange-600 transition"
                      >
                        Open Grid Builder
                      </a>
                    )}
                  </div>
                  
                  {q.type === 'visual' && (
                    <p className="text-sm text-orange-700 mb-3 italic">
                      Visual Rounds have 6 answers. Number your answers for this round (1-6).
                    </p>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <input
                      type="text"
                      placeholder="Category"
                      value={q.category || ''}
                      onChange={(e) => updateQuestion(idx, 'category', e.target.value)}
                      className="px-3 py-2 border rounded-lg"
                    />
                    <input
                      type="text"
                      placeholder="Question"
                      value={q.text || ''}
                      onChange={(e) => updateQuestion(idx, 'text', e.target.value)}
                      className="md:col-span-2 px-3 py-2 border rounded-lg"
                    />
                    <input
                      type="text"
                      placeholder={q.type === 'visual' ? "Answers (1. A, 2. B...)" : "Answer"}
                      value={q.answer || ''}
                      onChange={(e) => updateQuestion(idx, 'answer', e.target.value)}
                      className="px-3 py-2 border rounded-lg"
                    />
                  </div>

                  {q.type === 'visual' && (
                    <div className="mt-3">
                      <input
                        type="text"
                        placeholder="Image URL (e.g., https://quizzler.pro/img/visual.jpg)"
                        value={q.imageUrl || ''}
                        onChange={(e) => updateQuestion(idx, 'imageUrl', e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 bg-gray-200 text-gray-800 py-3 rounded-lg hover:bg-gray-300 transition font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="flex-1 bg-teal-600 text-white py-3 rounded-lg hover:bg-teal-700 transition font-semibold"
              >
                {isEditing ? 'Save Changes' : 'Create Game'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}